import { Cloudinary, Transformation } from 'cloudinary-core';
import isEmpty from 'lodash/isEmpty';
import queryString from 'query-string';

import { isBlank } from './helpers-ts';
import { FetchFormatMode, ITransformationOptions } from './image-models';
import { isImgix, removeSizesImgix } from './imgix-ts';

export const CLOUDINARY_URL_PREFIX = '//res.cloudinary.com/';

/**
 * Known Cloudinary transformation prefixes.
 * Used to identify transformation segments in URLs.
 */
const TRANSFORMATION_PREFIXES =
  /^(w|h|c|f|q|a|ar|b|bo|co|dpr|e|fl|g|l|o|r|t|u|x|y|z|ac|af|br|cs|d|dn|du|eo|fn|fps|if|ki|pg|so|sp|sr|st|vc|vs|zoom)_/;

/**
 * Checks if a URL segment is a Cloudinary transformation.
 *
 * @param segment - The URL segment to check
 * @returns `true` if the segment looks like a transformation (e.g. "w_100" or "w_100,h_100")
 */
export const isTransformationSegment = (segment: string): boolean => {
  if (TRANSFORMATION_PREFIXES.test(segment)) return true;

  // Check for chained transformations
  return segment.includes(',') && segment.split(',').some(part => TRANSFORMATION_PREFIXES.test(part));
};

/**
 * Checks if a URL is a Cloudinary URL.
 *
 * Supports various Cloudinary URL formats:
 * - `https://res.cloudinary.com/...`
 * - `http://res.cloudinary.com/...`
 * - `//res.cloudinary.com/...`
 * - Also supports `video.cloudinary.com` subdomain
 *
 * @param url - The URL to check
 * @returns `true` if the URL is a Cloudinary URL, `false` otherwise
 *
 * @example
 * isCloudinary("https://res.cloudinary.com/demo/image/upload/photo.jpg");
 * // Returns: true
 *
 * @example
 * isCloudinary("https://example.com/image.jpg");
 * // Returns: false
 */
export const isCloudinary = (url?: string): boolean => {
  if (isBlank(url)) return false;
  const trimmedUrl = url?.trim();
  if (isBlank(trimmedUrl)) return false;

  return /(?:(?:https?:)?\/\/)?(?:res|video)\.cloudinary\.com\//i.test(trimmedUrl);
};

/**
 * Extracts the cloud name from a Cloudinary URL.
 *
 * Supports various Cloudinary URL formats:
 * - `https://res.cloudinary.com/cloudname/...`
 * - `http://res.cloudinary.com/cloudname/...`
 * - `//res.cloudinary.com/cloudname/...`
 * - `res.cloudinary.com/cloudname/...`
 * - Also supports `video.cloudinary.com` subdomain
 *
 * @param url - The Cloudinary URL to parse
 * @returns The extracted cloud name, or `undefined` if the URL is invalid or not a Cloudinary URL
 *
 * @example
 * extractCloudinaryCloudName("https://res.cloudinary.com/mycloud/image/upload/v1/photo.jpg");
 * // Returns: "mycloud"
 */
export const extractCloudinaryCloudName = (url: string): string | undefined => {
  const trimmedUrl = url?.trim();
  if (isBlank(trimmedUrl)) return undefined;

  const cloudNameMatch = trimmedUrl.match(/(?:(?:https?:)?\/\/)?(?:res|video)\.cloudinary\.com\/([^/]+)\//i);
  return cloudNameMatch?.[1];
};

/**
 * All valid Cloudinary delivery types.
 *
 * - `upload`        — Assets uploaded directly to Cloudinary.
 * - `fetch`         — Remote URLs fetched and delivered on-the-fly.
 * - `private`       — Private assets requiring signed URLs for access.
 * - `authenticated` — Assets that require authentication tokens.
 * - `sprite`        — CSS sprite sheets generated from multiple images.
 * - `text`          — Dynamically generated text overlay images.
 * - `multi`         — Multi-page assets (e.g. animated GIFs, PDFs).
 */
const CLOUDINARY_DELIVERY_TYPES = ['upload', 'fetch', 'private', 'authenticated', 'sprite', 'text', 'multi'] as const;

/** Union of all Cloudinary delivery types. */
export type CloudinaryDeliveryType = typeof CLOUDINARY_DELIVERY_TYPES[number];

/**
 * Extracts the delivery type from a Cloudinary URL.
 *
 * Cloudinary supports various delivery types:
 * - `upload` - uploaded assets
 * - `fetch` - remote URLs fetched on-the-fly
 * - `private` - private assets
 * - `authenticated` - authenticated assets
 * - `sprite` - sprite images
 * - `text` - text images
 * - `multi` - multi-page assets
 *
 * @param url - The Cloudinary URL to parse
 * @returns The delivery type (e.g., 'upload', 'fetch'), or `undefined` if not found
 *
 * @example
 * extractCloudinaryDeliveryType("https://res.cloudinary.com/demo/image/upload/photo.jpg");
 * // Returns: "upload"
 *
 * @example
 * extractCloudinaryDeliveryType("https://res.cloudinary.com/demo/image/fetch/https://example.com/image.jpg");
 * // Returns: "fetch"
 */
export const extractCloudinaryDeliveryType = (url: string): CloudinaryDeliveryType | undefined => {
  const trimmedUrl = url?.trim();
  if (isBlank(trimmedUrl)) return undefined;

  // Match /image/{delivery_type}/ or /video/{delivery_type}/ or /raw/{delivery_type}/
  const deliveryMatch = trimmedUrl.match(/\/(?:image|video|raw)\/([^/]+)\//i);
  const deliveryType = deliveryMatch?.[1]?.toLowerCase();

  if (deliveryType && CLOUDINARY_DELIVERY_TYPES.includes(deliveryType as CloudinaryDeliveryType)) {
    return deliveryType as CloudinaryDeliveryType;
  }

  return undefined;
};

/**
 * Extracts the public ID from a Cloudinary URL.
 *
 * Parses the URL to find the public ID by:
 * - Extracting the path after `/upload/`
 * - Filtering out version segments (e.g., `v123`)
 * - Filtering out transformation segments (e.g., `w_300`, `h_200`, `c_limit`)
 * - Returning the remaining path as the public ID
 *
 * @param url - The Cloudinary URL to parse
 * @returns The extracted public ID (may include folder paths), or `undefined` if not found
 *
 * @example
 * extractCloudinaryPublicId("https://res.cloudinary.com/demo/image/upload/w_300/v1/folder/photo.jpg");
 * // Returns: "folder/photo.jpg"
 */
export const extractCloudinaryPublicId = (url: string): string | undefined => {
  const trimmedUrl = url?.trim();
  if (isBlank(trimmedUrl)) return undefined;

  const deliveryType = extractCloudinaryDeliveryType(trimmedUrl);
  if (!deliveryType) return undefined;

  const afterDeliveryMatch = trimmedUrl.match(new RegExp(`\\/${deliveryType}\\/([^?]+)`, 'i'));
  if (!afterDeliveryMatch) return undefined;

  let pathAfterUpload = afterDeliveryMatch[1];

  const segments = pathAfterUpload.split('/');
  const publicIdSegments: string[] = [];

  let foundPublicIdStart = false;

  for (const segment of segments) {
    if (foundPublicIdStart) {
      publicIdSegments.push(segment);
      continue;
    }

    // Skip version segments (vNNN) only before public ID path starts
    if (/^v\d+$/i.test(segment)) continue;

    // Check if segment is a transformation
    if (isTransformationSegment(segment)) continue;

    // This segment is the start of the public ID path
    foundPublicIdStart = true;
    publicIdSegments.push(segment);
  }

  const publicId = publicIdSegments.join('/');
  return publicId || undefined;
};

/**
 * Extracts the version number from a Cloudinary URL.
 *
 * Version can appear in two positions:
 * - Right after `/upload/` (e.g., `/upload/v123/...`)
 * - After transformations (e.g., `/upload/c_limit,w_64/v1/...`)
 *
 * @param url - The Cloudinary URL to parse
 * @returns The version number as an integer, or `undefined` if no version is found
 *
 * @example
 * extractCloudinaryVersion("https://res.cloudinary.com/demo/image/upload/v1234567890/photo.jpg");
 * // Returns: 1234567890
 */
export const extractCloudinaryVersion = (url: string): number | undefined => {
  const trimmedUrl = url?.trim();
  if (isBlank(trimmedUrl)) return undefined;

  const deliveryType = extractCloudinaryDeliveryType(trimmedUrl);
  if (!deliveryType) return undefined;

  // Check for version right after delivery type (e.g. /upload/v123/, /fetch/v123/)
  const afterDeliveryMatch = trimmedUrl.match(new RegExp(`\\/${deliveryType}\\/v(\\d+)\\/`, 'i'));
  if (afterDeliveryMatch) {
    return parseInt(afterDeliveryMatch[1], 10);
  }

  // Check for version after transformation segment (e.g., /c_limit,w_300/v123/ or /w_300/h_200/v123/)
  // Transformation segments contain underscore: prefix_value (like c_limit, w_300, h_200)
  const afterTransformMatch = trimmedUrl.match(/\/[a-z]{1,4}_[^/]+\/v(\d+)\//i);
  if (afterTransformMatch) {
    return parseInt(afterTransformMatch[1], 10);
  }

  return undefined;
};

/**
 * Extracts transformation options (width and height) from a Cloudinary URL.
 *
 * Parses the URL to find transformation segments between `/upload/` and the public ID.
 * Supports transformations both before and after the version segment.
 *
 * @param url - The Cloudinary URL to parse
 * @returns An object with `width` and/or `height` properties, or `undefined` if no transformations found
 *
 * @example
 * extractCloudinaryTransformation("https://res.cloudinary.com/demo/image/upload/w_300,h_200/photo.jpg");
 * // Returns: { width: 300, height: 200 }
 */
export const extractCloudinaryTransformation = (url: string): Transformation.Options | undefined => {
  const trimmedUrl = url?.trim();
  if (isBlank(trimmedUrl)) return undefined;

  const deliveryType = extractCloudinaryDeliveryType(trimmedUrl);
  if (!deliveryType) return undefined;

  // Extract everything after delivery type and before query params
  const afterDeliveryMatch = trimmedUrl.match(new RegExp(`\\/${deliveryType}\\/([^?]+)`, 'i'));
  if (!afterDeliveryMatch) return undefined;

  const pathAfterUpload = afterDeliveryMatch[1];
  const segments = pathAfterUpload.split('/');

  // Collect all transformation segments (excluding version and public ID parts)
  const transformationSegments: string[] = [];

  for (const segment of segments) {
    // Skip version segments
    if (/^v\d+$/i.test(segment)) continue;

    // Check if segment contains transformations
    if (isTransformationSegment(segment)) {
      transformationSegments.push(segment);
    }
  }

  if (transformationSegments.length === 0) return undefined;

  // Join all transformation segments for parsing
  const transformationString = transformationSegments.join(',');

  const options: Transformation.Options = {};

  // Match w_NNN pattern for width
  const widthMatch = transformationString.match(/(?:^|[,/])w_(\d+)/);
  if (widthMatch) {
    options.width = parseInt(widthMatch[1], 10);
  }

  // Match h_NNN pattern for height
  const heightMatch = transformationString.match(/(?:^|[,/])h_(\d+)/);
  if (heightMatch) {
    options.height = parseInt(heightMatch[1], 10);
  }

  // Match f_VALUE pattern for fetch_format
  const fetchFormatMatch = transformationString.match(/(?:^|[,/])f_([a-zA-Z0-9_]+)/);
  if (fetchFormatMatch) {
    options.fetch_format = fetchFormatMatch[1];
  }

  // Match dn_VALUE pattern for density
  const densityMatch = transformationString.match(/(?:^|[,/])dn_([0-9]+)/);
  if (densityMatch) {
    options.density = parseInt(densityMatch[1], 10);
  }

  // Match cs_VALUE pattern for color_space
  const colorSpaceMatch = transformationString.match(/(?:^|[,/])cs_([a-zA-Z0-9_\-.:]+)/);
  if (colorSpaceMatch) {
    options.color_space = colorSpaceMatch[1];
  }

  // Match fl_attachment:VALUE or fl_attachment pattern for attachment
  const attachmentMatch = transformationString.match(/(?:^|[,/])fl_attachment(?::([a-zA-Z0-9_\-.:]+))?(?:$|[,/.])/);
  if (attachmentMatch) {
    // If capture group 1 exists, it's the filename value. Otherwise it's boolean true.
    options.attachment = attachmentMatch[1] ? attachmentMatch[1] : true;
  }

  // Match c_VALUE pattern for crop
  const cropMatch = transformationString.match(/(?:^|[,/])c_([a-zA-Z0-9_]+)/);
  if (cropMatch) {
    options.crop = cropMatch[1];
  }

  // Match e_upscale pattern
  const upscaleMatch = transformationString.match(/(?:^|[,/])e_upscale(?:$|[,/.])/);
  if (upscaleMatch) {
    options.upscale = true;
  }

  return isEmpty(options) ? undefined : options;
};

/**
 * Builds Cloudinary transformation options from the provided dimensions and crop mode.
 *
 * Creates a transformation options object with width, height, and crop mode.
 * The crop mode is auto-set to "limit" when either width or height is provided
 * (unless crop is explicitly set or null).
 *
 * @param options - Optional object containing `width`, `height`, and/or `crop`
 * @param inputOptions - Optional additional Cloudinary transformation options to merge
 * @returns A Cloudinary `Transformation.Options` object ready to be used with the Cloudinary SDK
 *
 * @example
 * buildCloudinaryTransformationOptions({ width: 300, height: 200 });
 * // Returns: { width: 300, height: 200, crop: "limit" }
 *
 * @example
 * buildCloudinaryTransformationOptions({ width: 300 }, { quality: 80, format: "webp" });
 * // Returns: { width: 300, crop: "limit", quality: 80, format: "webp" }
 *
 * @example
 * buildCloudinaryTransformationOptions({ width: 300, crop: null });
 * // Returns: { width: 300, crop: undefined } - prevents auto-setting crop
 */
export const buildCloudinaryTransformationOptions = (
  options?: ITransformationOptions,
  inputOptions?: Transformation.Options
): Transformation.Options => {
  const { width, height, crop, disableAutoCrop } = options || {};
  const transformationOptions: Transformation.Options = { ...inputOptions };

  /**
   * Applies an option value to the transformation options.
   * - If value is truthy, sets it
   * - If value is null, sets to undefined (explicit clear)
   * - If value is undefined, leaves unchanged (inherits from inputOptions)
   */
  const applyOption = <K extends keyof Transformation.Options>(
    key: K,
    value: Transformation.Options[K] | null | undefined
  ) => {
    if (value) {
      transformationOptions[key] = value;
    } else if (value === null) {
      transformationOptions[key] = undefined;
    }
  };

  applyOption('width', width);
  applyOption('height', height);

  applyOption('crop', crop);
  applyOption('fetch_format', options?.fetch_format);
  applyOption('density', options?.density);
  applyOption('color_space', options?.color_space);

  if (options?.attachment) {
    if (typeof options.attachment === 'string') {
      applyOption('flags', `attachment:${options.attachment}`);
    } else {
      applyOption('flags', 'attachment');
    }
  } else if (options?.attachment === false || options?.attachment === null) {
    applyOption('flags', null);
  }

  // Auto-set crop to 'limit' when dimensions are provided and no crop is set
  if (crop === undefined && !disableAutoCrop && (width || height) && !inputOptions?.crop) {
    transformationOptions.crop = 'limit';
  }

  if (options?.compress) {
    transformationOptions.quality = 'auto';
  }

  return transformationOptions;
};

/**
 * Transforms a Cloudinary image URL with the specified dimension options.
 *
 * Parses the original URL to extract cloud name and public ID, then rebuilds
 * the URL with the specified transformations. Preserves any query string
 * parameters from the original URL.
 *
 * @param url - The original Cloudinary image URL (optional)
 * @param options - Optional transformation options with `width`, `height`, and/or `crop`
 * @returns The transformed URL with new dimensions, or the original URL if transformation fails
 *
 * @example
 * transformCloudinaryImage("https://res.cloudinary.com/demo/image/upload/photo.jpg", { width: 300 });
 * // Returns: "https://res.cloudinary.com/demo/image/upload/c_limit,w_300/photo.jpg"
 */
export const transformCloudinaryImage = (url?: string, options?: ITransformationOptions): string => {
  if (!url) return '';

  if (!options || isEmpty(options)) return url;

  if (!isCloudinary(url)) return url;

  const cloud_name = extractCloudinaryCloudName(url);
  const public_id = extractCloudinaryPublicId(url);
  if (!cloud_name || !public_id) return url;

  // Extract existing transformations from the URL
  const existingTransformations = extractCloudinaryTransformation(url);

  // Build new transformation options, merging with existing (new options override existing)
  const transformationOptions = buildCloudinaryTransformationOptions(options, existingTransformations);
  if (isEmpty(transformationOptions)) return url;

  // Preserve version from the original URL
  const version = extractCloudinaryVersion(url);
  if (version) {
    transformationOptions.version = String(version);
  }

  const cld = new Cloudinary({ cloud_name, secure: true });
  let transformedUrl = cld.url(public_id, transformationOptions);
  // const deliveryType = extractCloudinaryDeliveryType(url);

  // if (deliveryType !== 'fetch' && options.deliveryType === 'fetch') {
  //   transformedUrl = transformedUrl.replace(`/${deliveryType}/`, '/fetch/');
  // }

  const { query } = queryString.parseUrl(url);
  return isEmpty(query) ? transformedUrl : queryString.stringifyUrl({ url: transformedUrl, query });
};

/**
 * Returns a transformed Cloudinary image URL with the specified dimensions.
 *
 * @param url - The original Cloudinary image URL (optional)
 * @param width - The target width in pixels (optional)
 * @param height - The target height in pixels (optional)
 * @param fetchFormat - The fetch format to use (optional)
 * @returns The transformed URL with the specified dimensions, or empty string if url is empty
 */
export const resizeCloudinary = (
  url?: string,
  width?: number,
  height?: number,
  fetchFormat: FetchFormatMode = 'auto'
): string => {
  return transformCloudinaryImage(url, { width, height, fetch_format: fetchFormat });
};

/**
 * Returns a 64x64 transformed Cloudinary image URL.
 *
 * @param url - The original Cloudinary image URL (optional)
 * @returns The transformed URL with 64x64 dimensions, or empty string if url is empty
 */
export const resize64Cloudinary = (url?: string): string => resizeCloudinary(url, 64, 64, 'auto');

/**
 * Returns a 128x128 transformed Cloudinary image URL.
 *
 * @param url - The original Cloudinary image URL (optional)
 * @returns The transformed URL with 128x128 dimensions and auto format, or empty string if url is empty
 */
export const resize128Cloudinary = (url?: string): string => resizeCloudinary(url, 128, 128, 'auto');

/**
 * Returns a 256x256 transformed Cloudinary image URL.
 *
 * @param url - The original Cloudinary image URL (optional)
 * @returns The transformed URL with 256x256 dimensions and auto format, or empty string if url is empty
 */
export const resize256Cloudinary = (url?: string): string => resizeCloudinary(url, 256, 256, 'auto');

/**
 * Returns a 512x512 transformed Cloudinary image URL.
 *
 * @param url - The original Cloudinary image URL (optional)
 * @returns The transformed URL with 512x512 dimensions and auto format, or empty string if url is empty
 */
export const resize512Cloudinary = (url?: string): string => resizeCloudinary(url, 512, 512, 'auto');

/**
 * Removes width, height, and crop transformations from a Cloudinary image URL.
 *
 * @param url - The original Cloudinary image URL (optional)
 * @returns The transformed URL without size/crop transformations, or empty string if url is empty
 */
export const removeSizesCloudinary = (url?: string): string => {
  return transformCloudinaryImage(url, { width: null, height: null, crop: null });
};

/**
 * Generates a Cloudinary URL configured for print-ready image export.
 *
 * Applies the following transformations:
 * - ICC color profile (Adobe RGB by default)
 * - 300 DPI density for print quality
 * - TIFF format for lossless output
 * - Attachment flag to force download
 *
 * @param url - The original Cloudinary image URL (optional)
 * @param filename - Optional filename for the downloaded file (without extension)
 * @param iccProfile - ICC color profile to use (default: 'adobe.icc')
 * @returns The transformed URL configured for export, or empty string if url is empty
 *
 * @example
 * exportImageCloudinary("https://res.cloudinary.com/demo/image/upload/photo.jpg");
 * // Returns: "https://res.cloudinary.com/demo/image/upload/cs_icc:adobe.icc,dn_300,f_tiff,fl_attachment/photo.jpg"
 *
 * @example
 * exportImageCloudinary("https://res.cloudinary.com/demo/image/upload/photo.jpg", "my-export");
 * // Returns: "https://res.cloudinary.com/demo/image/upload/cs_icc:adobe.icc,dn_300,f_tiff,fl_attachment:my-export/photo.jpg"
 */
export const exportImageCloudinary = (url?: string, filename?: string, iccProfile: string = 'adobe.icc') => {
  if (!isImgix(url) && !isCloudinary(url)) return undefined;
  let cleanUrl = url;
  let cloudinaryUrl = url;
  const options: ITransformationOptions = {
    width: null,
    height: null,
    crop: null,
    color_space: `icc:${iccProfile}`,
    density: 300,
    fetch_format: 'tiff',
    attachment: filename || true,
    disableAutoCrop: true,
  };
  if (isImgix(url)) {
    // options.deliveryType = 'fetch';
    cleanUrl = removeSizesImgix(url);
    // Encode query string separator to ensure Cloudinary treats it as part of the fetch URL
    cleanUrl = cleanUrl.replace('?', '%3F');

    cloudinaryUrl = `https://res.cloudinary.com/foodla/image/upload/REPLACE_IT.png`;
    cloudinaryUrl = transformCloudinaryImage(cloudinaryUrl, options);
    cloudinaryUrl = cloudinaryUrl.replace('/image/upload/', '/image/fetch/');
    cloudinaryUrl = cloudinaryUrl.replace('REPLACE_IT.png', cleanUrl);
    return cloudinaryUrl;
  } else if (isCloudinary(url)) {
    return transformCloudinaryImage(cleanUrl, options);
  }
  return undefined;
};

/**
 * Adds a pixel density transformation to a Cloudinary image URL.
 *
 * @param url - The original Cloudinary image URL (optional)
 * @param density - The target pixel density (optional)
 * @returns The transformed URL with the specified density, or empty string if url is empty
 */
export const addDensityCloudinary = (url?: string, density?: number): string => {
  return transformCloudinaryImage(url, { density });
};

/**
 * Metadata returned by Cloudinary's `fl_getinfo` transformation.
 *
 * @see {@link https://cloudinary.com/documentation/image_transformations#retrieving_image_metadata}
 */
export interface CloudinaryMetadata {
  /** Metadata for the delivered (transformed) image. */
  output: {
    width: number;
    height: number;
    format: string;
    bytes: number;
    url: string;
  };
  /** Metadata for the original (pre-transformation) image, if available. */
  input?: {
    width: number;
    height: number;
    format: string;
    bytes: number;
    url: string;
  };
  /** Contextual metadata key-value pairs attached to the asset. */
  context?: Record<string, any>;
  /** EXIF and other embedded image metadata. */
  image_metadata?: Record<string, any>;
  /** Any additional fields returned by Cloudinary. */
  [key: string]: any;
}

/**
 * Get info about image from Cloudinary
 *
 * It uses the fl_getinfo transformation to retrieve image metadata in JSON format.
 * https://cloudinary.com/documentation/image_transformations#retrieving_image_metadata
 *
 * @param url - The Cloudinary URL
 * @returns info object or undefined
 */
export const getInfoCloudinary = async (url?: string): Promise<CloudinaryMetadata | undefined> => {
  try {
    const trimmedUrl = url?.trim();
    if (isBlank(trimmedUrl)) return undefined;

    if (!isCloudinary(trimmedUrl)) return undefined;

    const cloud_name = extractCloudinaryCloudName(trimmedUrl);
    const public_id = extractCloudinaryPublicId(trimmedUrl);
    if (!cloud_name || !public_id) return undefined;

    // We want version if present
    const version = extractCloudinaryVersion(trimmedUrl);

    const cld = new Cloudinary({ cloud_name, secure: true });

    const options: Transformation.Options = {
      flags: 'getinfo',
    };

    if (version) {
      options.version = String(version);
    }

    const infoUrl = cld.url(public_id, options);

    // Now fetch
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), 15000);

    try {
      const response = await fetch(infoUrl, {
        signal: controller.signal,
      });
      clearTimeout(id);

      if (response.ok) {
        const json: CloudinaryMetadata = await response.json();
        return json;
      }
    } catch (err) {
      clearTimeout(id);
      throw err;
    }
  } catch (error) {
    console.error(error);
  }

  return undefined;
};
